import { PhotoSwipeLightbox } from 'vendor';

class GalleryLightbox extends HTMLElement {
  #lightbox;
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.addEventListener('click', this.#onClick.bind(this), {
      signal: this.#abortController.signal
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#lightbox?.destroy();
    this.#lightbox = undefined;
  }

  get #items() {
    return Array.from(this.querySelectorAll('[data-gallery-item]'));
  }

  get #lightboxInstance() {
    if (this.#lightbox) {
      return this.#lightbox;
    }

    this.#lightbox = new PhotoSwipeLightbox({
      pswpModule: () => import('photoswipe'),
      bgOpacity: 1,
      closeTitle: window.themeVariables?.strings?.closeGallery,
      zoomTitle: window.themeVariables?.strings?.zoomGallery,
      errorMsg: window.themeVariables?.strings?.errorGallery,
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      close: false,
      zoom: false
    });

    this.#lightbox.on('uiRegister', () => {
      this.#lightbox.pswp.ui.registerElement({
        name: 'close-button',
        className: 'circle-button circle-button--xl hover:animate-icon-block',
        ariaLabel: window.themeVariables?.strings?.closeGallery,
        order: 2,
        isButton: true,
        html: `
          <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon" viewBox="0 0 16 16">
            <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/>
          </svg>
        `,
        onClick: () => {
          this.#lightbox.pswp.close();
        }
      });

      if (this.#lightbox.pswp.options.dataSource.length > 1) {
        this.#lightbox.pswp.ui.registerElement({
          name: 'previous-button',
          className: 'circle-button hover:animate-icon-inline',
          ariaLabel: window.themeVariables?.strings?.previous,
          order: 1,
          isButton: true,
          html: `
            <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
              <path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"/>
            </svg>
          `,
          onClick: () => {
            this.#lightbox.pswp.prev();
          }
        });

        this.#lightbox.pswp.ui.registerElement({
          name: 'next-button',
          className: 'circle-button hover:animate-icon-inline',
          ariaLabel: window.themeVariables?.strings?.next,
          order: 3,
          isButton: true,
          html: `
            <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
              <path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"/>
            </svg>
          `,
          onClick: () => {
            this.#lightbox.pswp.next();
          }
        });
      }
    });

    this.#lightbox.addFilter('thumbEl', (thumbEl, data) => data.thumbnailElement);
    this.#lightbox.init();

    return this.#lightbox;
  }

  #onClick(event) {
    if (event.defaultPrevented) {
      return;
    }

    const item = event.target.closest('[data-gallery-item]');

    if (!item || !this.contains(item)) {
      return;
    }

    event.preventDefault();

    const items = this.#items;
    const index = items.indexOf(item);

    if (index < 0) {
      return;
    }

    const dataSource = items.map((galleryItem) => {
      const image = galleryItem.querySelector('img');
      const fullSrc = galleryItem.getAttribute('data-pswp-src') || image?.currentSrc || image?.src;
      const width = parseInt(galleryItem.getAttribute('data-pswp-width') || image?.getAttribute('width'), 10);
      const height = parseInt(galleryItem.getAttribute('data-pswp-height') || image?.getAttribute('height'), 10);

      return {
        thumbnailElement: image,
        src: fullSrc,
        srcset: image?.srcset,
        msrc: image?.currentSrc || image?.src,
        width: width || image?.naturalWidth || 1200,
        height: height || image?.naturalHeight || 1200,
        alt: image?.alt || '',
        thumbCropped: true
      };
    });

    this.#lightboxInstance.loadAndOpen(index, dataSource);
  }
}

if (!window.customElements.get('gallery-lightbox')) {
  window.customElements.define('gallery-lightbox', GalleryLightbox);
}
